import type { HTMLAttributes, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import iconPerson from '../../../assets/student/Icon-person.png';
import iconXpStudent from '../../../assets/student/Icon-xp-student.png';
import { iconStudentStar } from '../../assets';
import { useTheme } from '../../theme';
import type { StudentRankingCurrentUser, StudentRankingEntry } from './types';

export interface StudentRankingPanelProps extends Omit<
  HTMLAttributes<HTMLElement>,
  'title'
> {
  title?: ReactNode;
  /** Se omitido, usa `Icon-xp-student.png` */
  headerIcon?: ReactNode;
  entries: StudentRankingEntry[];
  currentUser?: StudentRankingCurrentUser;
}

export function StudentRankingPanel({
  title,
  headerIcon,
  entries,
  currentUser,
  style,
  ...rest
}: StudentRankingPanelProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const resolvedTitle =
    title !== undefined ? title : t('student_dashboard.ranking_title');
  const rk = theme.palette.studentDashboard.ranking;
  const aside = theme.palette.studentDashboard.aside;
  const L = theme.layout.studentDashboard;

  const minEntryPosition =
    entries.length > 0
      ? Math.min(...entries.map(e => e.position))
      : Number.POSITIVE_INFINITY;
  /** Utilizador com melhor posição que qualquer linha do `top` (ex.: 1.º) — bloco destacado em cima para manter ordem e marcação. */
  const showCurrentUserFirst =
    currentUser != null && currentUser.position < minEntryPosition;

  const showRankGapEllipsis =
    currentUser != null &&
    currentUser.position > L.rankingGapAfterRank &&
    !showCurrentUserFirst;

  const currentUserRow =
    currentUser != null ? (
      <div
        style={{
          boxSizing: 'border-box',
          width: L.rankingRowWidth,
          height: L.rankingRowHeight,
          borderRadius: theme.shape.borderRadius.md,
          border: `${theme.shape.borderWidth.medium} solid ${rk.currentUserBorder}`,
          padding: `0 ${theme.spacing.sm}`,
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing.sm,
          backgroundColor: rk.currentUserBackground,
        }}
      >
        <strong
          style={{
            fontSize: theme.typography.fontSize.base,
            fontWeight: theme.typography.fontWeight.bold,
            minWidth: L.rankingPositionMinWidth,
            textAlign: 'center',
            color: rk.nameText,
          }}
        >
          {currentUser.position}
        </strong>
        <span
          style={{
            width: L.rankingMedalIconSize,
            height: L.rankingMedalIconSize,
            borderRadius: theme.shape.borderRadius.full,
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
            backgroundColor: rk.medalIconBackground,
          }}
        >
          {currentUser.medalSlot != null ? (
            currentUser.medalSlot
          ) : (
            <img
              src={iconPerson}
              alt=""
              width={Math.round(L.rankingMedalIconSize * 0.5)}
              height={Math.round(L.rankingMedalIconSize * 0.5)}
              style={{ display: 'block', objectFit: 'contain' }}
              aria-hidden
            />
          )}
        </span>
        <span
          style={{
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
          }}
        >
          <strong
            style={{
              fontSize: theme.typography.fontSize.sm,
              color: rk.nameText,
            }}
          >
            {currentUser.label}
          </strong>
          <small style={{ color: rk.scoreText }}>
            {t('student_dashboard.ranking_xp', { xp: currentUser.xp })}
          </small>
        </span>
      </div>
    ) : null;

  return (
    <aside
      style={{
        flex: `0 1 ${L.asideWidth}px`,
        width: L.asideWidth,
        maxWidth: '100%',
        borderRadius: theme.shape.borderRadius.lg,
        overflow: 'hidden',
        backgroundColor: theme.palette.background.paper,
        boxShadow: aside.boxShadow,
        ...style,
      }}
      {...rest}
    >
      <div
        style={{
          backgroundColor: rk.headerBackground,
          padding: `${theme.spacing.md} ${theme.spacing.md}`,
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing.sm,
        }}
      >
        {headerIcon != null ? (
          <span style={{ display: 'flex', alignItems: 'center' }} aria-hidden>
            {headerIcon}
          </span>
        ) : (
          <img
            src={iconXpStudent}
            alt=""
            width={L.rankingHeaderIconSize}
            height={L.rankingHeaderIconSize}
            style={{ display: 'block', objectFit: 'contain' }}
            aria-hidden
          />
        )}
        <span
          style={{
            fontSize: theme.typography.fontSize.lg,
            fontWeight: theme.typography.fontWeight.bold,
            color: rk.headerText,
          }}
        >
          {resolvedTitle}
        </span>
      </div>

      <div
        style={{
          padding: theme.spacing.md,
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing.sm,
        }}
      >
        {showCurrentUserFirst ? currentUserRow : null}

        {entries.map(user => {
          const isTopThree = user.position <= 3;
          return (
            <div
              key={user.id}
              style={{
                boxSizing: 'border-box',
                width: L.rankingRowWidth,
                height: L.rankingRowHeight,
                display: 'flex',
                alignItems: 'center',
                gap: theme.spacing.sm,
                borderRadius: theme.shape.borderRadius.md,
                backgroundColor: isTopThree
                  ? rk.topThreeRowBackground
                  : rk.rowBackground,
                padding: `0 ${theme.spacing.sm}`,
                marginBottom: theme.spacing.xs,
                marginTop: theme.spacing.xs,
              }}
            >
              <span
                style={{
                  minWidth: L.rankingPositionMinWidth,
                  textAlign: 'center',
                  color: rk.nameText,
                  fontWeight: theme.typography.fontWeight.semibold,
                }}
              >
                {user.position}
              </span>
              <span
                style={{
                  position: 'relative',
                  flexShrink: 0,
                  width: L.rankingAvatarSize,
                  height: L.rankingAvatarSize,
                }}
              >
                <span
                  style={{
                    width: L.rankingAvatarSize,
                    height: L.rankingAvatarSize,
                    borderRadius: theme.shape.borderRadius.full,
                    display: 'grid',
                    placeItems: 'center',
                    boxSizing: 'border-box',
                    backgroundColor: rk.avatarBackground,
                    border: `${theme.shape.borderWidth.medium} solid ${rk.avatarBorder}`,
                    color: rk.avatarText,
                    fontSize: theme.typography.fontSize.xs,
                    fontWeight: theme.typography.fontWeight.bold,
                  }}
                >
                  {user.initials}
                </span>
                {isTopThree ? (
                  <span
                    style={{
                      position: 'absolute',
                      top: -rk.starBadgeOffset,
                      right: -rk.starBadgeOffset,
                      width: L.rankingStarBadgeSize,
                      height: L.rankingStarBadgeSize,
                      borderRadius: theme.shape.borderRadius.full,
                      boxSizing: 'border-box',
                      backgroundColor: rk.starBadgeBackground,
                      boxShadow: rk.starBadgeShadow,
                      display: 'grid',
                      placeItems: 'center',
                    }}
                    aria-hidden
                  >
                    <img
                      src={iconStudentStar}
                      alt=""
                      width={L.rankingStarIconSize}
                      height={L.rankingStarIconSize}
                      style={{ display: 'block', objectFit: 'contain' }}
                    />
                  </span>
                ) : null}
              </span>
              <span
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  minWidth: 0,
                }}
              >
                <strong
                  style={{
                    fontSize: theme.typography.fontSize.base,
                    fontWeight: theme.typography.fontWeight.semibold,
                    color: rk.nameText,
                  }}
                >
                  {user.name}
                </strong>
                <small
                  style={{
                    color: rk.scoreText,
                    fontWeight: theme.typography.fontWeight.normal,
                    fontSize: theme.typography.fontSize.xs,
                  }}
                >
                  {t('student_dashboard.ranking_xp', { xp: user.xp })}
                </small>
              </span>
            </div>
          );
        })}
        {showRankGapEllipsis ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: theme.spacing['4xl'],
            }}
            aria-hidden
          >
            <span style={{ color: rk.scoreTextMuted }}>
              {t('student_dashboard.ranking_gap_ellipsis')}
            </span>
          </div>
        ) : null}

        {!showCurrentUserFirst ? currentUserRow : null}
      </div>
    </aside>
  );
}
